import { ColorPicker, FontIcon, ICalloutContentStyles, mergeStyleSets, useTheme } from '@fluentui/react';
import { IPPrimaryButton, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalTextStylesBold } from 'globalStyles';
import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Curated palette for tag colors (distinguishable, pleasant, works in light + dark). The default is a
// palette member so a brand-new tag always starts on a real, selected color (never "none"/blank).
export const TAG_COLOR_PALETTE = [
  '#D13438',
  '#CA5010',
  '#C19C00',
  '#498205',
  '#038387',
  '#0078D4',
  '#4F6BED',
  '#8764B8',
  '#C239B3',
  '#69797E',
];
export const DEFAULT_TAG_COLOR = '#0078D4';

interface IColorFieldProps {
  color: string;
  onChange: (color: string) => void;
  disabled?: boolean;
}

//
// A reusable, "label-color" style color picker: a row of preset swatches plus a custom-color swatch
// that opens the full picker. Used by both tag create and edit so there is one implementation.
//
const ColorField = (props: IColorFieldProps) => {
  const { t } = useTranslation(['tag', 'translation']);
  const theme = useTheme();
  const [customCallout, setCustomCallout] = useState<boolean>(false);
  const calloutId = useMemo(() => 'tag-color-custom-' + Math.random().toString(36).substring(7), []);
  const swatchRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const color = props.color || DEFAULT_TAG_COLOR;
  const isPreset = TAG_COLOR_PALETTE.some((c) => c.toLowerCase() === color.toLowerCase());

  const onSwatchKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const direction =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!direction) return;

    event.preventDefault();
    const next = (index + direction + TAG_COLOR_PALETTE.length + 1) % (TAG_COLOR_PALETTE.length + 1);
    swatchRefs.current[next]?.focus();
  };

  const styles = mergeStyleSets({
    row: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 4 },
    swatch: {
      width: 26,
      height: 26,
      borderRadius: '50%',
      cursor: 'pointer',
      padding: 0,
      border: '2px solid transparent',
      boxShadow: theme.effects.elevation4,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 100ms ease',
      selectors: {
        ':hover': { transform: 'scale(1.1)' },
        ':disabled': { cursor: 'default', opacity: 0.5, transform: 'none' },
      },
    },
    swatchSelected: {
      border: `2px solid ${theme.palette.white}`,
      outline: `2px solid ${theme.palette.neutralPrimary}`,
    },
    check: { color: theme.palette.white, fontSize: 12 },
    custom: {
      width: 26,
      height: 26,
      borderRadius: '50%',
      cursor: 'pointer',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px dashed ${theme.palette.neutralSecondary}`,
      background: theme.semanticColors.bodyBackground,
      color: theme.palette.neutralPrimary,
      selectors: { ':hover': { background: theme.palette.neutralLighter } },
    },
  });

  const calloutStyle: Partial<ICalloutContentStyles> = { root: { padding: '10px 12px', maxWidth: 600 } };

  return (
    <IPStack>
      <IPText styles={globalTextStylesBold}>{t('tag:Picker.ColorLabel')}</IPText>
      <div className={styles.row} role="group" aria-label={t('tag:Picker.ColorLabel')}>
        {TAG_COLOR_PALETTE.map((c, index) => {
          const selected = isPreset && c.toLowerCase() === color.toLowerCase();

          return (
            <button
              key={c}
              ref={(el) => {
                swatchRefs.current[index] = el;
              }}
              type="button"
              aria-pressed={selected}
              aria-label={c}
              tabIndex={selected ? 0 : -1}
              disabled={props.disabled}
              className={selected ? `${styles.swatch} ${styles.swatchSelected}` : styles.swatch}
              style={{ backgroundColor: c }}
              onClick={() => props.onChange(c)}
              onKeyDown={(event) => onSwatchKeyDown(event, index)}
            >
              {selected && <FontIcon iconName="CheckMark" className={styles.check} aria-hidden={true} />}
            </button>
          );
        })}
        {/* Custom color: a dashed swatch that opens the full picker. When the active color is not a
            preset (e.g. an existing tag's custom hex), this swatch shows that color and reads selected. */}
        <button
          id={calloutId}
          ref={(el) => {
            swatchRefs.current[TAG_COLOR_PALETTE.length] = el;
          }}
          type="button"
          disabled={props.disabled}
          aria-pressed={!isPreset}
          aria-label={t('tag:Picker.CustomColor')}
          title={t('tag:Picker.CustomColor')}
          tabIndex={!isPreset ? 0 : -1}
          className={!isPreset ? `${styles.swatch} ${styles.swatchSelected}` : styles.custom}
          style={!isPreset ? { backgroundColor: color } : undefined}
          onClick={() => setCustomCallout(true)}
          onKeyDown={(event) => onSwatchKeyDown(event, TAG_COLOR_PALETTE.length)}
        >
          <FontIcon iconName={!isPreset ? 'CheckMark' : 'Color'} className={!isPreset ? styles.check : undefined} aria-hidden={true} />
        </button>
      </div>
      <IPCallout
        styles={calloutStyle}
        hidden={!customCallout}
        setInitialFocus
        gapSpace={0}
        target={`#${calloutId}`}
        onDismiss={() => setCustomCallout(false)}
        dismissOnTargetClick
      >
        <ColorPicker
          color={color}
          onChange={(ev, c) => props.onChange(c ? c.str : color)}
          alphaType={'none'}
          showPreview
        />
        <IPPrimaryButton autoFocus onClick={() => setCustomCallout(false)}>
          {t('translation:General.Button.Close')}
        </IPPrimaryButton>
      </IPCallout>
    </IPStack>
  );
};

export default ColorField;
